import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useRouter } from 'next/router';
import { useAuthContext } from '@havenengineering/module-shared-owners-ui/dist/contexts/auth';

import { fetchWrapper, withApiBaseUrl } from '../helpers/fetch';
import { LettingAccount, LettingSummary } from '../types/lettings';

const ROUTE_CHANGE_START = 'routeChangeStart';

const AUTHENTICATED_GET: Parameters<typeof fetchWrapper>[1] = {
  method: 'GET',
  credentials: 'include',
};

const requestLettingSummary = (accountId: number): Promise<LettingSummary> =>
  fetchWrapper(
    withApiBaseUrl(`/letting/letting-summary/${accountId}`),
    AUTHENTICATED_GET
  );

const requestLettingAccount = (
  accountNumber: string
): Promise<LettingAccount> =>
  fetchWrapper(
    withApiBaseUrl(`/letting/account?accountNumber=${accountNumber}`),
    AUTHENTICATED_GET
  );

type RenewalStateRefresh = {
  isLet2OwnRenewal: boolean;
  accountId?: number;
  accountNumber?: string;
  onLettingSummary: (lettingSummary: LettingSummary) => void;
  onLettingAccount: (lettingAccount: LettingAccount) => void;
};

// Plot can freeze an account mid-journey while the provider stays mounted;
// a failed re-read keeps the last known state rather than ending the journey.
const useRenewalStateRefreshOnNavigation = ({
  isLet2OwnRenewal,
  accountId,
  accountNumber,
  onLettingSummary,
  onLettingAccount,
}: RenewalStateRefresh): void => {
  const router = useRouter();

  useEffect(() => {
    if (!isLet2OwnRenewal || !accountId || !accountNumber) {
      return;
    }

    let isListening = true;
    let latestRefresh = 0;

    const refreshRenewalState = async (): Promise<void> => {
      latestRefresh += 1;
      const refresh = latestRefresh;

      const [summary, account] = await Promise.allSettled([
        requestLettingSummary(accountId),
        requestLettingAccount(accountNumber),
      ]);

      if (!isListening || refresh !== latestRefresh) {
        return;
      }

      if (summary.status === 'fulfilled') {
        onLettingSummary(summary.value);
      }

      if (account.status === 'fulfilled') {
        onLettingAccount(account.value);
      }
    };

    router.events.on(ROUTE_CHANGE_START, refreshRenewalState);

    return () => {
      isListening = false;
      router.events.off(ROUTE_CHANGE_START, refreshRenewalState);
    };
  }, [
    isLet2OwnRenewal,
    accountId,
    accountNumber,
    onLettingSummary,
    onLettingAccount,
    router.events,
  ]);
};

export type LettingsContextState = {
  lettingSummary: LettingSummary | null;
  isLet2OwnRenewal: boolean;
  isLoading: boolean;
  let2OwnRenewalCompleted: boolean;
  refetchLettingSummary: () => Promise<void>;
  partialPaymentMade: boolean;
  lettingAccount: LettingAccount | null;
};

const LettingsContext = createContext<LettingsContextState>({
  lettingSummary: null,
  isLet2OwnRenewal: false,
  isLoading: true,
  let2OwnRenewalCompleted: false,
  refetchLettingSummary: async () => {},
  partialPaymentMade: false,
  lettingAccount: null,
});

const LettingsContextProvider: React.FunctionComponent<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const { activeAccount } = useAuthContext();
  const [lettingSummary, setLettingSummary] = useState<LettingSummary | null>(
    null
  );
  const [lettingAccount, setLettingAccount] = useState<LettingAccount | null>(
    null
  );

  const fetchLettingAccount = useCallback(async () => {
    if (activeAccount?.accountNo) {
      try {
        setLettingAccount(await requestLettingAccount(activeAccount.accountNo));
      } catch (err) {
        setLettingAccount(null);
      }
    }
  }, [activeAccount]);

  const fetchLettingSummary = useCallback(async () => {
    if (!activeAccount?.accountID) {
      return;
    }

    try {
      setLettingSummary(await requestLettingSummary(activeAccount.accountID));
    } catch (error) {
      setLettingSummary(null);
    } finally {
      setIsLoading(false);
    }
  }, [activeAccount]);

  useEffect(() => {
    fetchLettingSummary();
  }, [activeAccount, fetchLettingSummary]);

  useEffect(() => {
    fetchLettingAccount();
  }, [activeAccount, fetchLettingAccount]);

  const isLet2OwnRenewal = Boolean(
    lettingSummary?.let2Own && lettingSummary?.isRenewing
  );

  useRenewalStateRefreshOnNavigation({
    isLet2OwnRenewal,
    accountId: activeAccount?.accountID,
    accountNumber: activeAccount?.accountNo,
    onLettingSummary: setLettingSummary,
    onLettingAccount: setLettingAccount,
  });

  const context = useMemo(
    () => ({
      lettingSummary,
      isLet2OwnRenewal,
      isLoading,
      partialPaymentMade: lettingSummary?.partialPaymentMade || false,
      let2OwnRenewalCompleted:
        (lettingSummary?.let2Own && lettingSummary?.renewalCompleted) || false,
      refetchLettingSummary: () => fetchLettingSummary(),
      lettingAccount,
    }),
    [
      lettingSummary,
      isLet2OwnRenewal,
      isLoading,
      fetchLettingSummary,
      lettingAccount,
    ]
  );

  return (
    <LettingsContext.Provider value={context}>
      {children}
    </LettingsContext.Provider>
  );
};

export { LettingsContext, LettingsContextProvider };
